/**
 * data/countries.ts — E.164 dialing codes.
 *
 * Curated list, not exhaustive. The list intentionally lifts:
 *   * United States first (default selection, US-only sponsor-bank
 *     product; anything else is friction for the 99% path).
 *   * Then the 50 most common source countries for US-facing fintech
 *     signups, alphabetised by name.
 *
 * The picker's search input handles typos and partial matches so a
 * missing country is a "type in the +code" fallback rather than a
 * dead-end. If a country is missing from this list, the user can
 * still submit the E.164 number by typing the full `+cc...` — the
 * picker's default US +1 is just a nudge, not a constraint.
 *
 * Flags are represented via Unicode regional-indicator pairs so we
 * don't ship a flag asset bundle. Every modern browser renders these
 * as flag emoji.
 */

export interface Country {
  /** ISO 3166-1 alpha-2. Used as the stable key. */
  code: string;
  /** Human-readable name for the picker list. */
  name: string;
  /** E.164 dial prefix, WITHOUT the leading `+`. */
  dial: string;
  /** Unicode flag emoji. */
  flag: string;
}

/** Convert an ISO alpha-2 code to a regional-indicator flag emoji. */
function flagOf(iso: string): string {
  const A = 0x1f1e6;
  const codes = iso.toUpperCase().split("").map((c) => A + (c.charCodeAt(0) - 65));
  return String.fromCodePoint(...codes);
}

const RAW: Array<Omit<Country, "flag">> = [
  // United States lifted to position 0 — it is the default selection.
  { code: "US", name: "United States",       dial: "1"   },
  // Then alphabetised.
  { code: "AR", name: "Argentina",           dial: "54"  },
  { code: "AU", name: "Australia",           dial: "61"  },
  { code: "AT", name: "Austria",             dial: "43"  },
  { code: "BD", name: "Bangladesh",          dial: "880" },
  { code: "BE", name: "Belgium",             dial: "32"  },
  { code: "BR", name: "Brazil",              dial: "55"  },
  { code: "CA", name: "Canada",              dial: "1"   },
  { code: "CL", name: "Chile",               dial: "56"  },
  { code: "CN", name: "China",               dial: "86"  },
  { code: "CO", name: "Colombia",            dial: "57"  },
  { code: "DK", name: "Denmark",             dial: "45"  },
  { code: "EG", name: "Egypt",               dial: "20"  },
  { code: "FI", name: "Finland",             dial: "358" },
  { code: "FR", name: "France",              dial: "33"  },
  { code: "DE", name: "Germany",             dial: "49"  },
  { code: "GH", name: "Ghana",               dial: "233" },
  { code: "GR", name: "Greece",              dial: "30"  },
  { code: "HK", name: "Hong Kong",           dial: "852" },
  { code: "IN", name: "India",               dial: "91"  },
  { code: "ID", name: "Indonesia",           dial: "62"  },
  { code: "IE", name: "Ireland",             dial: "353" },
  { code: "IL", name: "Israel",              dial: "972" },
  { code: "IT", name: "Italy",               dial: "39"  },
  { code: "JP", name: "Japan",               dial: "81"  },
  { code: "KE", name: "Kenya",               dial: "254" },
  { code: "MY", name: "Malaysia",            dial: "60"  },
  { code: "MX", name: "Mexico",              dial: "52"  },
  { code: "NL", name: "Netherlands",         dial: "31"  },
  { code: "NZ", name: "New Zealand",         dial: "64"  },
  { code: "NG", name: "Nigeria",             dial: "234" },
  { code: "NO", name: "Norway",              dial: "47"  },
  { code: "PK", name: "Pakistan",            dial: "92"  },
  { code: "PE", name: "Peru",                dial: "51"  },
  { code: "PH", name: "Philippines",         dial: "63"  },
  { code: "PL", name: "Poland",              dial: "48"  },
  { code: "PT", name: "Portugal",            dial: "351" },
  { code: "RO", name: "Romania",             dial: "40"  },
  { code: "SA", name: "Saudi Arabia",        dial: "966" },
  { code: "SG", name: "Singapore",           dial: "65"  },
  { code: "ZA", name: "South Africa",        dial: "27"  },
  { code: "KR", name: "South Korea",         dial: "82"  },
  { code: "ES", name: "Spain",               dial: "34"  },
  { code: "SE", name: "Sweden",              dial: "46"  },
  { code: "CH", name: "Switzerland",         dial: "41"  },
  { code: "TW", name: "Taiwan",              dial: "886" },
  { code: "TH", name: "Thailand",            dial: "66"  },
  { code: "TR", name: "Turkey",              dial: "90"  },
  { code: "AE", name: "United Arab Emirates", dial: "971" },
  { code: "GB", name: "United Kingdom",      dial: "44"  },
  { code: "VN", name: "Vietnam",             dial: "84"  },
];

export const COUNTRIES: Country[] = RAW.map((c) => ({ ...c, flag: flagOf(c.code) }));

export const DEFAULT_COUNTRY: Country = COUNTRIES[0]; // United States

/** Look up a country by ISO alpha-2. Falls back to DEFAULT_COUNTRY. */
export function findCountry(code: string): Country {
  return COUNTRIES.find((c) => c.code === code.toUpperCase()) ?? DEFAULT_COUNTRY;
}

/**
 * Filter for the picker search box. Matches name (case-insensitive
 * substring) or dial prefix. Empty query returns the full list.
 */
export function filterCountries(query: string): Country[] {
  const q = query.trim().toLowerCase();
  if (!q) return COUNTRIES;
  return COUNTRIES.filter((c) => {
    if (c.name.toLowerCase().includes(q)) return true;
    if (c.dial.includes(q.replace(/^\+/, ""))) return true;
    if (c.code.toLowerCase().includes(q)) return true;
    return false;
  });
}
